import { Text } from '../Text';
import type { TextExtension } from '../types';

/**
 * Extension interface providing query parameter manipulation for any path or URL string.
 * Works with both relative paths (e.g. `/api/foo`) and absolute URLs.
 */
export interface PathExtension extends Text {
  /**
   * Creates a new path with additional or modified query parameters.
   * Existing parameters are preserved unless overwritten.
   * Works with both relative paths and absolute URLs.
   *
   * @param params - Key-value pairs to add or update in the query string.
   * @returns A new path with the updated query parameters.
   *
   * @example
   * const t = $$$('/api/data', asPath());
   * t.withParams({ version: '2' }).$$; // '/api/data?version=2'
   */
  withParams(
    params: Record<string, string | number | undefined>,
  ): PathExtension;

  /**
   * Gets all query parameters as a record.
   *
   * @returns An object containing all query parameters.
   *
   * @example
   * const t = $$$('/api/data?a=1&b=2', asPath());
   * t.getParams(); // { a: '1', b: '2' }
   */
  getParams(): Record<string, string>;
}

/**
 * Creates an extension that adds query parameter manipulation to any path or URL string.
 * Unlike {@link asURL}, this works with both relative paths (e.g. `/api/foo`) and
 * absolute URLs. Preserves hash fragments.
 *
 * @example
 * const t = $$$('/api/data', asPath());
 * t.withParams({ version: '2' }).$$; // '/api/data?version=2'
 *
 * @example
 * const t = $$$('https://example.com/path?a=1', asPath());
 * t.withParams({ b: '2' }).$$; // 'https://example.com/path?a=1&b=2'
 */
export function asPath(): TextExtension<PathExtension> {
  return (text) => {
    const $$ = text.$$;
    const [pathAndQuery = '', ...hashParts] = $$.split('#');
    const hash = hashParts.length > 0 ? `#${hashParts.join('#')}` : '';
    const [base, ...queryParts] = pathAndQuery.split('?');
    const searchParams = new URLSearchParams(queryParts.join('?'));

    return {
      withParams(
        params: Record<string, string | number | boolean | undefined>,
      ): PathExtension {
        const newParams = new URLSearchParams(searchParams);
        for (const [key, value] of Object.entries(params)) {
          if (value === undefined) {
            newParams.delete(key);
          } else {
            newParams.set(key, String(value));
          }
        }
        const newSearch = newParams.toString();
        const newPath = `${base}${newSearch ? `?${newSearch}` : ''}${hash}`;
        return Text.of(newPath).extend(asPath());
      },

      getParams(): Record<string, string> {
        const result: Record<string, string> = {};
        searchParams.forEach((value, key) => {
          result[key] = value;
        });
        return result;
      },
    };
  };
}
